import { useState, useEffect } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";

export default function StudentAssets({ user: propUser }) {
  const [user] = useUserData();
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("available");

  const currentUser = propUser || user;

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "assets"), (snapshot) => {
      const assetData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAssets(assetData);
    });

    return unsubscribe;
  }, []);

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || asset.status === statusFilter;

    return matchesSearch && matchesStatus && asset.status === "available";
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "available": return "bg-green-100 text-green-800";
      case "in-use": return "bg-blue-100 text-blue-800";
      case "maintenance": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Asset Library</h1>
          <p className="text-gray-600">Browse and request available assets</p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search assets by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="in-use">In Use</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
          </div>
        </div>

        {/* Assets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">No assets found</h3>
              <p className="text-gray-500">Try adjusting your search terms</p>
            </div>
          ) : (
            filteredAssets.map(asset => (
              <div key={asset.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
                {/* Asset Image/QR */}
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  {asset.qrUrl ? (
                    <img
                      src={asset.qrUrl}
                      alt="Asset QR Code"
                      className="w-32 h-32 object-contain"
                    />
                  ) : (
                    <div className="text-4xl text-gray-400">📦</div>
                  )}
                </div>

                {/* Asset Details */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {asset.name}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                      {asset.status}
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {asset.description}
                  </p>

                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <p><span className="font-medium">Category:</span> {asset.categoryLabel || asset.category}</p>
                    <p><span className="font-medium">Location:</span> {asset.building} - {asset.room}</p>
                    <p><span className="font-medium">Serial:</span> {asset.serialNumber || "N/A"}</p>
                  </div>

                  {/* Request Button */}
                  <button
                    disabled={asset.status !== "available"}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      asset.status === "available"
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      // TODO: Navigate to request form with this asset
                      console.log("Request asset:", asset.id);
                    }}
                  >
                    {asset.status === "available" ? "Request Asset" : "Not Available"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
